import { v4 as uuidv4 } from 'uuid';
import {
    add, boundsOf, makeLabel, normalizeAngle, rectsIntersect, rotatePoint, roundPoint, SEAT_RADIUS, seatAbs, seatKey, shapeBounds, sub,
    type Rect,
} from './geometry';
import type { GaType, LabelScheme, ObjectShape, Plan, Point, Row, Seat, SectionLabels, SectionLayout, Stage, Zone } from './types';

export const DEFAULT_SECTION_LABELS: SectionLabels = {
    sectionPrefix: 'Section',
    rowPrefix: 'Row',
    seatPrefix: 'Seat',
    showSection: true,
    showRows: 'all',
    rowScheme: 'upper',
    rowStart: 'A',
    rowReverse: false,
    seatScheme: 'numeric',
    seatStart: '1',
    seatReverse: false,
};

export const SECTION_LABEL_GAP = 30;

const clampInt = (v: number, min: number, max: number) => Math.max(min, Math.min(max, Math.round(Number.isFinite(v) ? v : min)));

export const defaultSectionLayout = (rows: number, seatsPerRow: number, seatSpacing = 25, rowSpacing = 30): SectionLayout => ({
    rows: clampInt(rows, 1, 200),
    seatsPerRow: clampInt(seatsPerRow, 1, 300),
    seatSpacing,
    rowSpacing,
    curve: 0,
    skew: 0,
    align: 'center',
    rotation: 0,
});

export const isSection = (zone: Zone | undefined): boolean => !!zone?.section;

const isAlpha = (scheme: LabelScheme) => scheme === 'upper' || scheme === 'lower';

export const labelStartIndex = (scheme: LabelScheme, start: string): number => {
    const t = (start ?? '').trim();
    if (isAlpha(scheme)) {
        const letters = t.toUpperCase().replace(/[^A-Z]/g, '');
        if (!letters) return 1;
        let n = 0;
        for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
        return n;
    }
    const n = parseInt(t, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
};

export const defaultStartFor = (scheme: LabelScheme) => (scheme === 'upper' ? 'A' : scheme === 'lower' ? 'a' : scheme === 'even' ? '2' : '1');

const toRad = (deg: number) => (deg * Math.PI) / 180;

export const sectionPoints = (layout: SectionLayout, counts: number[]): Point[][] => {
    const { seatSpacing: s, rowSpacing: rs, align } = layout;
    const widest = Math.max(1, ...counts);
    const span = (widest - 1) * s;
    const skewShift = Math.tan(toRad(Math.max(-60, Math.min(60, layout.skew || 0)))) * rs;
    const sweep = toRad(Math.max(-300, Math.min(300, layout.curve || 0)));
    const curved = Math.abs(sweep) > 0.005 && span > 0;
    const sign = Math.sign(sweep);
    const r0 = !curved ? 0 : span / Math.abs(sweep) + (sign < 0 ? (counts.length - 1) * rs : 0);

    const rows = counts.map((n, i) => {
        const offset = align === 'left' ? -span / 2 : align === 'right' ? span / 2 - (n - 1) * s : -((n - 1) * s) / 2;
        return Array.from({ length: n }, (_, j) => {
            const x = offset + j * s + i * skewShift;
            if (!curved) return { x, y: i * rs };
            const r = r0 + sign * i * rs;
            const t = x / r;
            return { x: r * Math.sin(t), y: -sign * r0 + sign * r * Math.cos(t) };
        });
    });
    const box = boundsOf(rows.flat());
    if (!box) return rows;
    const c = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    return rows.map(r => r.map(p => sub(p, c)));
};

const newSeat = (category: string): Seat => {
    const id = uuidv4();
    return { seat_number: '', uuid: id, seat_guid: id, position: { x: 0, y: 0 }, category };
};

export type SectionPatch = {
    layout?: Partial<SectionLayout>;
    labels?: Partial<SectionLabels>;
    counts?: Record<number, number>;
};

export const layoutSection = (zone: Zone, patch: SectionPatch = {}, category = ''): Zone => {
    const base = zone.section ?? defaultSectionLayout(zone.rows.length || 1, Math.max(1, ...zone.rows.map(r => r.seats.length)));
    const layout: SectionLayout = { ...base, ...patch.layout };
    layout.rows = clampInt(layout.rows, 1, 200);
    layout.seatsPerRow = clampInt(layout.seatsPerRow, 1, 300);
    layout.seatSpacing = Math.max(15, Math.min(200, layout.seatSpacing || 25));
    layout.rowSpacing = Math.max(15, Math.min(200, layout.rowSpacing || 30));
    layout.curve = Math.max(-300, Math.min(300, Math.round(layout.curve || 0)));
    layout.skew = Math.max(-60, Math.min(60, Math.round(layout.skew || 0)));
    layout.rotation = normalizeAngle(layout.rotation || 0);
    const labels: SectionLabels = { ...DEFAULT_SECTION_LABELS, ...zone.labels, ...patch.labels };

    const counts = Array.from({ length: layout.rows }, (_, i) => {
        const override = patch.counts?.[i];
        if (override !== undefined) return clampInt(override, 1, 300);
        if (patch.layout?.seatsPerRow !== undefined) return layout.seatsPerRow;
        return zone.rows[i]?.seats.length || layout.seatsPerRow;
    });

    const points = sectionPoints(layout, counts);
    const rowStart = labelStartIndex(labels.rowScheme, labels.rowStart);
    const seatStart = labelStartIndex(labels.seatScheme, labels.seatStart);
    let fallbackCategory = category;

    const rows: Row[] = points.map((pts, i) => {
        const prev = zone.rows[i];
        const n = pts.length;
        const seats = pts.map((p, j) => {
            const old = prev?.seats[j];
            if (old?.category) fallbackCategory = old.category;
            const seat = old ?? newSeat(prev?.seats[prev.seats.length - 1]?.category ?? fallbackCategory);
            return {
                ...seat,
                seat_number: makeLabel(labels.seatReverse ? n - 1 - j : j, labels.seatScheme, seatStart),
                position: roundPoint(rotatePoint(p, { x: 0, y: 0 }, layout.rotation)),
            };
        });
        return {
            ...(prev ?? {}),
            uuid: prev?.uuid ?? uuidv4(),
            row_number: makeLabel(labels.rowReverse ? points.length - 1 - i : i, labels.rowScheme, rowStart),
            row_number_position: 'both',
            position: { x: 0, y: 0 },
            seats,
        };
    });

    return { ...zone, section: layout, labels, rows };
};

export const createSectionZone = (
    name: string,
    center: Point,
    opts: { rows: number; seatsPerRow: number; seatSpacing?: number; rowSpacing?: number; category?: string; labels?: Partial<SectionLabels> },
): Zone =>
    layoutSection(
        {
            name,
            code: name,
            position: roundPoint(center),
            rows: [],
            section: defaultSectionLayout(opts.rows, opts.seatsPerRow, opts.seatSpacing, opts.rowSpacing),
            labels: { ...DEFAULT_SECTION_LABELS, ...opts.labels },
        },
        {},
        opts.category ?? '',
    );

export const sectionSize = (opts: { rows: number; seatsPerRow: number; seatSpacing?: number; rowSpacing?: number }) => ({
    width: (Math.max(1, opts.seatsPerRow) - 1) * (opts.seatSpacing ?? 25) + 20,
    height: (Math.max(1, opts.rows) - 1) * (opts.rowSpacing ?? 30) + 20,
});

export const sectionLabelAnchor = (zone: Zone): { point: Point; rotation: number } | null => {
    const rot = zone.section?.rotation ?? 0;
    const origin = zone.position ?? { x: 0, y: 0 };
    const local: Point[] = [];
    zone.rows.forEach(r => r.seats.forEach(s => local.push(rotatePoint(sub(seatAbs(zone, r, s), origin), { x: 0, y: 0 }, -rot))));
    const box = boundsOf(local);
    if (!box) return null;
    const p = { x: box.x + box.width / 2, y: box.y - SECTION_LABEL_GAP };
    return { point: add(origin, rotatePoint(p, { x: 0, y: 0 }, rot)), rotation: rot };
};

export const zoneSeatKeys = (zone: Zone): string[] => zone.rows.flatMap(r => r.seats.map(seatKey));

export const fullZoneIndexes = (plan: Plan, keys: Set<string>): Set<number> => {
    const out = new Set<number>();
    if (!keys.size) return out;
    plan.zones.forEach((zone, zi) => {
        const seats = zoneSeatKeys(zone);
        if (seats.length && seats.every(k => keys.has(k))) out.add(zi);
    });
    return out;
};

export const nextItemLabel = (plan: Plan): string => {
    let max = 0;
    const take = (name?: string) => {
        const m = /^\D{0,3}(\d+)$/.exec((name ?? '').trim());
        if (m) max = Math.max(max, parseInt(m[1], 10));
    };
    plan.zones.forEach(z => z.section && take(z.name));
    (plan.stages ?? []).forEach(s => (s.variant === 'table' || s.variant === 'ga') && take(s.name));
    return String(max + 1);
};

export const ensureFreeZone = (plan: Plan, preferred: number): { plan: Plan; zi: number } => {
    if (plan.zones[preferred] && !plan.zones[preferred].section) return { plan, zi: preferred };
    const free = plan.zones.findIndex(z => !z.section);
    if (free >= 0) return { plan, zi: free };
    return {
        plan: { ...plan, zones: [...plan.zones, { name: 'Floor', code: 'FL', position: { x: 0, y: 0 }, rows: [] }] },
        zi: plan.zones.length,
    };
};

export const rebaseZone = (zone: Zone, position: Point): Zone => {
    const d = sub(zone.position ?? { x: 0, y: 0 }, position);
    return {
        ...zone,
        position: roundPoint(position),
        rows: zone.rows.map(r => ({ ...r, position: roundPoint(add(r.position ?? { x: 0, y: 0 }, d)) })),
    };
};

export const duplicateSections = (target: Plan, source: Plan, zis: number[], offset: Point): { plan: Plan; seats: Set<string> } => {
    const seats = new Set<string>();
    let plan = target;
    zis.forEach(zi => {
        const src = source.zones[zi];
        if (!src) return;
        const name = nextItemLabel(plan);
        const zone: Zone = {
            ...src,
            name,
            code: name,
            position: roundPoint(add(src.position ?? { x: 0, y: 0 }, offset)),
            rows: src.rows.map(r => ({
                ...r,
                uuid: uuidv4(),
                seats: r.seats.map(s => {
                    const id = uuidv4();
                    seats.add(id);
                    return { ...s, uuid: id, seat_guid: id };
                }),
            })),
        };
        plan = { ...plan, zones: [...plan.zones, zone] };
    });
    return { plan, seats };
};

export const totalCapacity = (plan: Plan): number =>
    plan.zones.reduce((n, z) => n + z.rows.reduce((m, r) => m + r.seats.length, 0), 0)
    + (plan.stages ?? []).reduce((n, s) => n + (s.variant === 'ga' ? Math.max(0, s.capacity ?? 0) : 0), 0);

export const createGaArea = (center: Point, name: string, opts: { capacity: number; gaType: GaType; showIcon: boolean }): Stage => ({
    id: uuidv4(),
    name,
    type: 'square',
    variant: 'ga',
    position: roundPoint({ x: center.x - 110, y: center.y - 70 }),
    size: { width: 220, height: 140 },
    rotation: 0,
    color: '#D1D5DB',
    strokeColor: '#9CA3AF',
    strokeWidth: 2,
    curveRadius: 0,
    capacity: clampInt(opts.capacity, 1, 100000),
    gaType: opts.gaType,
    showIcon: opts.showIcon,
});

export const regularPolygon = (sides: number, radius: number): Point[] => {
    const n = clampInt(sides, 3, 12);
    return Array.from({ length: n }, (_, i) => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        return { x: radius * Math.cos(a), y: radius * Math.sin(a) };
    });
};

export const createObject = (
    center: Point,
    opts: { shape: ObjectShape; name: string; icon?: string; showIcon: boolean; sides?: number },
): Stage => {
    const common = { id: uuidv4(), name: opts.name, rotation: 0, icon: opts.icon, showIcon: opts.showIcon };
    if (opts.shape === 'line') {
        return {
            ...common,
            variant: 'line',
            type: 'polygon',
            position: roundPoint({ x: center.x - 100, y: center.y }),
            size: { width: 200, height: 0 },
            points: [{ x: 0, y: 0 }, { x: 200, y: 0 }],
            color: 'transparent',
            strokeColor: '#374151',
            strokeWidth: 3,
            showIcon: false,
        };
    }
    if (opts.shape === 'polygon') {
        const abs = regularPolygon(opts.sides ?? 6, 70).map(p => add(p, center));
        const minX = Math.min(...abs.map(p => p.x));
        const minY = Math.min(...abs.map(p => p.y));
        const maxX = Math.max(...abs.map(p => p.x));
        const maxY = Math.max(...abs.map(p => p.y));
        return {
            ...common,
            variant: 'object',
            type: 'polygon',
            position: roundPoint({ x: minX, y: minY }),
            size: { width: Math.round(maxX - minX), height: Math.round(maxY - minY) },
            points: abs.map(p => roundPoint({ x: p.x - minX, y: p.y - minY })),
            color: '#E5E7EB',
            strokeColor: '#6B7280',
            strokeWidth: 2,
        };
    }
    const size = opts.shape === 'circle' ? { width: 130, height: 130 } : { width: 180, height: 100 };
    return {
        ...common,
        variant: 'object',
        type: opts.shape,
        position: roundPoint({ x: center.x - size.width / 2, y: center.y - size.height / 2 }),
        size,
        color: '#E5E7EB',
        strokeColor: '#6B7280',
        strokeWidth: 2,
        curveRadius: opts.shape === 'square' ? 4 : undefined,
    };
};

export const regenSection = (plan: Plan, zi: number, patch: SectionPatch): Plan => {
    const zone = plan.zones[zi];
    if (!zone?.section) return plan;
    return { ...plan, zones: plan.zones.map((z, i) => (i === zi ? layoutSection(z, patch) : z)) };
};

export const zoneBounds = (zone: Zone): Rect | null => {
    const b = boundsOf(zone.rows.flatMap(r => r.seats.map(s => seatAbs(zone, r, s))), SEAT_RADIUS + 4);
    if (!b || !zone.section) return b;
    const top = SECTION_LABEL_GAP + 12;
    return { x: b.x, y: b.y - top, width: b.width, height: b.height + top };
};

const planObstacles = (plan: Plan): Rect[] => {
    const out: Rect[] = [];
    plan.zones.forEach(z => {
        if (z.section) {
            const b = zoneBounds(z);
            if (b) out.push(b);
            return;
        }
        z.rows.forEach(r => {
            const b = boundsOf(r.seats.map(s => seatAbs(z, r, s)), SEAT_RADIUS + 4);
            if (b) out.push(b);
        });
    });
    [...(plan.stages ?? []), ...(plan.entrances ?? [])].forEach(s => out.push(shapeBounds(s)));
    return out;
};

export const findFreeOffset = (plan: Plan, rect: Rect, gap = 30): Point => {
    const obstacles = planObstacles(plan);
    const hits = (dx: number, dy: number) => {
        const r = { x: rect.x + dx - gap / 2, y: rect.y + dy - gap / 2, width: rect.width + gap, height: rect.height + gap };
        return obstacles.some(o => rectsIntersect(o, r));
    };
    if (!hits(0, 0)) return { x: 0, y: 0 };
    const sx = (rect.width + gap) / 2;
    const sy = (rect.height + gap) / 2;
    const candidates: { x: number; y: number; d: number }[] = [];
    for (let i = -12; i <= 12; i++) {
        for (let j = -12; j <= 12; j++) {
            if (i || j) candidates.push({ x: Math.round((i * sx) / 5) * 5, y: Math.round((j * sy) / 5) * 5, d: Math.hypot(i * sx, j * sy) });
        }
    }
    candidates.sort((a, b) => a.d - b.d);
    const free = candidates.find(c => !hits(c.x, c.y));
    if (free) return { x: free.x, y: free.y };
    const all = boundsOf(obstacles.flatMap(o => [{ x: o.x, y: o.y }, { x: o.x + o.width, y: o.y + o.height }]));
    return all ? { x: Math.round(all.x + all.width + gap - rect.x), y: 0 } : { x: 0, y: 0 };
};
